import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Table, Tooltip } from "antd";
import clsx from "clsx";
import {
  VectorIcon,
  StatusSignedIcon,
  StatusPendingIcon,
  StatusDeclinedIcon,
  CommentIcon,
} from "@/shared/assets/svg/icons";
import { queryClient } from "@/shared/lib/react-query";
import { UserBubble } from "@shared/ui";
import { journalsQueries } from "../../api/journals.queries";
import { components } from "../../api/openapi/journal-service";
import "./journals-subscribers-table.scss";

type Signer = components["schemas"]["SignerInfo"];

interface IJournalsSubscribersTable {
  titleId?: string;
  sectionOneId?: string;
  sectionTwoId?: string;
  sectionThreeId?: string;
  sectionFourId?: string;
  sectionFiveId?: string;
  type?:
    | "title"
    | "sectionOne"
    | "sectionTwo"
    | "sectionThree"
    | "sectionFour"
    | "sectionFive";
}

const getSignersQuery = ({
  titleId = "",
  sectionOneId = "",
  sectionTwoId = "",
  sectionThreeId = "",
  sectionFourId = "",
  sectionFiveId = "",
  type,
}: {
  titleId?: string;
  sectionOneId?: string;
  sectionTwoId?: string;
  sectionThreeId?: string;
  sectionFourId?: string;
  sectionFiveId?: string;
  type:
    | "title"
    | "sectionOne"
    | "sectionTwo"
    | "sectionThree"
    | "sectionFour"
    | "sectionFive";
}) => {
  switch (type) {
    case "title":
      return journalsQueries.getJournalSignersList(titleId);
    case "sectionOne":
      return journalsQueries.getSectionOneSignersList(sectionOneId);
    case "sectionTwo":
      return journalsQueries.getSectionTwoSignersList(sectionTwoId);
    case "sectionThree":
      return journalsQueries.getSectionThreeSignersList(sectionThreeId);
    case "sectionFour":
      return journalsQueries.getSectionFourSignersList(sectionFourId);
    case "sectionFive":
      return journalsQueries.getSectionFiveSignersList(sectionFiveId);
    default:
      return journalsQueries.getJournalSignersList(titleId);
  }
};

const STATUS_MAP = {
  NEW: {
    title: "Новый",
    Icon: null,
  },
  PENDING: {
    title: "Ожидает подписания",
    Icon: StatusPendingIcon,
  },
  DONE: {
    title: "Подписано",
    Icon: StatusSignedIcon,
  },
  REJECTED: {
    title: "Отклонено подписание",
    Icon: StatusDeclinedIcon,
  },
  AWAITING_SIGNATURE: {
    title: "Ожидает подписания",
    Icon: StatusPendingIcon,
  },
  SIGNED: {
    title: "Подписано",
    Icon: StatusSignedIcon,
  },
};

type SignerUserInfoExtended = NonNullable<
  Signer["userInfo"]
> & {
  surname?: string;
  firstName?: string;
  middleName?: string;
};

const formatSignerName = (userInfo?: SignerUserInfoExtended) =>
  `${userInfo?.userSurname || userInfo?.surname || ""} ${userInfo?.userFirstName || userInfo?.firstName || ""} ${userInfo?.userMiddleName || userInfo?.middleName || ""}`
    .split("")
    .filter(Boolean)
    .join("");

export const JournalsSubscribersTable = ({
  titleId,
  sectionOneId,
  sectionTwoId,
  sectionThreeId,
  sectionFourId,
  sectionFiveId,
  type,
}: IJournalsSubscribersTable) => {
  const resolvedType = type || "title";
  const query = getSignersQuery({
    titleId,
    sectionOneId,
    sectionTwoId,
    sectionThreeId,
    sectionFourId,
    sectionFiveId,
    type: resolvedType,
  });
  const [expanded, setExpanded] = useState(false);

  const resourceIdReady =
    (resolvedType === "title" && !!titleId) ||
    (resolvedType === "sectionOne" && !!sectionOneId) ||
    (resolvedType === "sectionTwo" && !!sectionTwoId) ||
    (resolvedType === "sectionThree" && !!sectionThreeId) ||
    (resolvedType === "sectionFour" && !!sectionFourId) ||
    (resolvedType === "sectionFive" && !!sectionFiveId);

  const { data: signers, isPending: isSignersPending, isFetching: isSignersFetching } =
    // @ts-expect-error union query options по типу раздела
    useQuery({
      ...query,
      enabled: resourceIdReady,
      staleTime: 5 * 60_000,
    });

  const isSignersTableLoading =
    expanded && (isSignersPending || isSignersFetching);

  const columns = [
    {
      title: "ФИО",
      key: "name",
      render: (_: any, record: Signer) => {
        return (
          <UserBubble userId={record?.userInfo?.userId}>
            <Button type="link">
              {formatSignerName(record?.userInfo as SignerUserInfoExtended | undefined)}
            </Button>
          </UserBubble>
        );
      },
    },

    {
      title: "Роль",
      render: (_: any, record: any) => {
        return record.role ?? record.representativeRole ?? "-";
      },
    },
    {
      title: "Статус подписания",
      dataIndex: "status",
      key: "status",
      render: (status: keyof typeof STATUS_MAP, signer: Signer) => {
        const statusInfo = STATUS_MAP[status];
        if (!statusInfo)
          return (
            <div className="journals-subscribers-status">
              <div>{status}</div>
            </div>
          );

        const { title, Icon } = statusInfo;

        return (
          <span className="journals-subscribers-status">
            {Icon && <Icon className="journals-subscribers-status__icon" />}
            {title}
            {signer?.comment ? (
              <Tooltip title={signer?.comment}>
                <div>
                  <CommentIcon className="journals-subscribers-status__comment-icon" />
                </div>
              </Tooltip>
            ) : null}
          </span>
        );
      },
    },
    {
      title: "Дата время",
      dataIndex: "updateDate",
      key: "updateDate",
      render: (_: any, record: Signer) => {
        const date = new Date(record.updateDate || "");
        const formattedDate = date.toLocaleDateString("ru-RU");
        const formattedTime = date.toLocaleTimeString("ru-RU", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });

        return (
          <div className="journals-subscribers-date">
            <div className="journals-subscribers-date__date">
              {formattedDate}
            </div>
            <div className="journals-subscribers-date__time">
              {formattedTime} МСК
            </div>
          </div>
        );
      },
    },
  ];

  const handleExpand = () => {
    if (!expanded) {
      queryClient.invalidateQueries({ queryKey: query.queryKey });
    }
    setExpanded(!expanded);
  };

  return (
    <div
      className="table-container table-container-scroll-tab journals-subscribers-table"
      id="journals-subscribers-table"
    >
      <div onClick={handleExpand} className="table-header">
        <span
          className={clsx(
            "expandable-icon-container",
            expanded && "expandable-icon-container--expanded",
          )}
        >
          <div className={clsx("expandable-icon", expanded && "open")}>
            <VectorIcon />
          </div>
          <div className="expandable-icon-container__title">Список подписантов</div>
        </span>
      </div>
      <div
        className={clsx("table-body", expanded ? "open" : "closed", "table-journal")}
      >
        <Table<Signer>
          dataSource={
            (signers as components["schemas"]["SignersInfo"] | undefined)?.signers
          }
          columns={columns}
          loading={isSignersTableLoading}
          pagination={false}
          bordered={false}
          locale={{
            emptyText: (
              <div className="journals-subscribers-empty">
                {resolvedType === "title" ? (
                  <>
                    Список подписантов будет сформирован после отправки на
                    согласование титульного листа
                  </>
                ) : null}
                {resolvedType === "sectionOne" ? (
                  <>
                    Список подписантов соответствует уполномоченным
                    представителем лица, осуществляющего строительство, ремонт ,
                    кап. ремонт
                  </>
                ) : null}
                {resolvedType === "sectionTwo" ? (
                  <>
                    Список подписантов соответствует уполномоченным
                    представителем застройщика, лица ответственного за
                    эксплуатацию здания, сооружения и (или ) регионального
                    оператора
                  </>
                ) : null}
              </div>
            ),
          }}
          rowKey={(val) => val.representativeId!}
        />
      </div>
    </div>
  );
};
